import { stepDetailInputVisible } from './medicalQuestionnaireChoiceOptions';

const EMPTY_DISPLAY = '—';

function labelForChoiceValue(step, value) {
  const choices = step?.choiceOptions || [];
  const key = String(value ?? '').trim();
  if (!key) return '';
  const byId = choices.find((c) => String(c.id) === key);
  if (byId) return byId.label;
  const lower = key.toLowerCase();
  const byLabel = choices.find(
    (c) => String(c.label) === key || String(c.label).toLowerCase() === lower
  );
  return byLabel?.label ?? key;
}

function appendUnitIfNeeded(questionText, display) {
  if (!display || display === EMPTY_DISPLAY) return display;
  const lowerQ = (questionText || '').toLowerCase();
  const lowerD = display.toLowerCase();
  for (const unit of ['cm', 'kg']) {
    if (lowerQ.includes(`(${unit})`) && !lowerD.includes(unit)) {
      return `${display} ${unit}`;
    }
  }
  return display;
}

/** Human-readable answer for the summary accordion (from flow step + answers state). */
export function formatDisplayAnswerForStep(step, answers) {
  if (!step || !answers) return EMPTY_DISPLAY;

  const main = answers[step.key];
  const detail = step.detailKey ? answers[step.detailKey] : '';

  if (step.multi) {
    const ids = Array.isArray(main) ? main : [];
    if (!ids.length) return EMPTY_DISPLAY;
    return ids.map((id) => labelForChoiceValue(step, id)).join(', ');
  }

  if (step.type === 'yesNoWithTextarea') {
    const mainStr = String(main ?? '').trim();
    const detailStr = String(detail ?? '').trim();
    if (!mainStr && !detailStr) return EMPTY_DISPLAY;
    const label = mainStr ? labelForChoiceValue(step, main) : '';
    if (mainStr && stepDetailInputVisible(step, main) && detailStr) {
      return `${label} — ${detailStr}`;
    }
    return label || detailStr || EMPTY_DISPLAY;
  }

  if (step.type === 'choiceList') {
    const mainStr = String(main ?? '').trim();
    if (!mainStr) return EMPTY_DISPLAY;
    return labelForChoiceValue(step, main);
  }

  const scalar = Array.isArray(main) ? '' : String(main ?? '').trim();
  const detailStr = String(detail ?? '').trim();
  if (!scalar && !detailStr) return EMPTY_DISPLAY;

  if (step.type === 'number' || String(step.validation?.type || '').toLowerCase() === 'number') {
    return appendUnitIfNeeded(step.question, scalar);
  }

  return scalar;
}

/**
 * Map questionnaire schema sections + answers into summary page UI rows.
 * @param {Array<{ title: string, questions: object[] }>} schemaSections
 * @param {Record<string, unknown>} answers
 * @param {Record<number, boolean>} completionMap
 */
export function buildSummaryUiSections(schemaSections, answers, completionMap = {}) {
  return (schemaSections || []).map((section, sectionIdx) => {
    const questions = section.questions || [];
    const count = questions.length;
    return {
      title: section.title,
      subtitle: count === 1 ? '1 vraag' : `${count} vragen`,
      complete: Boolean(completionMap[sectionIdx]),
      qa: questions.map((question) => ({
        q: question.question,
        a: formatDisplayAnswerForStep(question, answers),
      })),
    };
  });
}

function isBackendSummarySectionsMap(sections) {
  if (!sections || typeof sections !== 'object' || Array.isArray(sections)) return false;
  return Object.values(sections).some(
    (block) =>
      block &&
      typeof block === 'object' &&
      Array.isArray(block.questions) &&
      (block.questions.length === 0 ||
        (block.questions[0] != null && typeof block.questions[0] === 'object'))
  );
}

function unwrapSummaryEnvelope(envelope) {
  let inner = envelope?.data !== undefined ? envelope.data : envelope;
  for (let i = 0; i < 5; i++) {
    if (!inner || typeof inner !== 'object' || Array.isArray(inner)) break;
    if (inner.sections && isBackendSummarySectionsMap(inner.sections)) break;
    if (inner.data !== undefined) {
      inner = inner.data;
      continue;
    }
    break;
  }
  return inner;
}

/**
 * Read-only summary page rows from GET medical_questionnaire/summary (display `answer` per question).
 */
export function buildSummaryUiSectionsFromSummaryEnvelope(envelope) {
  const inner = unwrapSummaryEnvelope(envelope);
  const sectionsMap = inner?.sections;
  if (!sectionsMap || !isBackendSummarySectionsMap(sectionsMap)) return [];

  return Object.entries(sectionsMap).map(([title, block]) => {
    const questions = Array.isArray(block?.questions) ? block.questions : [];
    const count = questions.length;
    const complete = String(block?.status || '').trim().toLowerCase() === 'completed';
    return {
      title,
      subtitle: count === 1 ? '1 vraag' : `${count} vragen`,
      complete,
      qa: [...questions]
        .sort((a, b) => (a.order || 0) - (b.order || 0))
        .map((question) => {
          const display = question.answer;
          const text =
            display != null && String(display).trim() !== ''
              ? String(display).trim()
              : EMPTY_DISPLAY;
          return {
            q: String(question.question ?? '').trim() || '—',
            a: text,
          };
        }),
    };
  });
}
